import * as React from "react";
import {useContext, useEffect, useMemo, useRef} from "react";
import {useTranslation} from "react-i18next";
import {AgGridReact} from "ag-grid-react";
import {
    ColDef,
    ExcelStyle,
    GetRowIdParams,
    GridApi,
    GridOptions,
    IServerSideGetRowsParams,
    ITooltipParams,
} from "ag-grid-enterprise";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {FilterDef} from "../../../../../constants/FinanceModels";
import {
    STATE_ACTIVE_FILTERS,
    STATE_TABLE_UPDATING,
} from "../../atoms/PaymentRequestAtoms";
import {
    formatCurrency,
    formatDate,
    formatDateWithoutTime,
} from "../../../../../utils/FormatUtils";
import {
    getLocalGridState,
    setLocalGridState,
} from "../../../../../utils/GridUtils";
import {STATE_SUMMARY_UPDATING} from "../../../../../constants/FinanceRecoil";
import AgGridCustomTable from "../../../../../../../util/component/AgGridCustomTable";
import EmptyState from "../../../../../../../util/component/EmptyState";
import {NumericSummary} from "../../../../../component/NumericSummary";
import ColouredChip from "../../../../../component/ColouredChip";
import * as Const from "../filters/PaymentRequestConstants";
import FinanceDynamicActions from "../../../../../component/FinanceDynamicActions";
import {PAYMENT_REQUEST_VIEW_DETAIL} from "../../../../../utils/FinanceScope";
import {
    useAgGridLocale,
    useHasScope,
} from "../../../../../../../config/globalConfig";
import {Button, IconButton, Tooltip} from "@mui/material";
import {DescriptionRounded, FileDownloadRounded} from "@mui/icons-material";
import {useLocation, useNavigate} from "react-router-dom";
import {usePaymentRequestRepository} from "../../../../../api/repositories/PaymentRequestRepository";
import dayjs from "dayjs";
import {ExportDataAsExcelBase} from "../../../../../utils/ExportDataAsExcelBase";
import {setSnackbar} from "../../../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../../../util/state/globalVariables";
import DownloadRoundedIcon from "@mui/icons-material/Download";
import {GlobalContext} from "../../../../../../../util/state/GlobalContextProvider";
import {handleColour, handleStatus} from "../filters/PaymentRequestConstants";
import {useCarSharingRepository} from "../../../../../api/repositories/CarSharingRepository";

const PaymentRequestTable = (props: any) => {
    const UNIQUE_ID = "fnn-payments-request";
    const hasScope = useHasScope;
    const {t} = useTranslation("finance", {
        keyPrefix: "payments.payment-request",
    });
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const customerId = queryParams.get("customerId");
    const invoiceNumber = queryParams.get("invoiceNumber");
    const correlationName = queryParams.get("productType");
    const correlationId = queryParams.get("productId");
    const {getPaymentsRequests, getSummaryRequest, downloadInvoice} = usePaymentRequestRepository();
    const {getPaymentsRequestsCs, getSummaryRequestCs, handleRedirect} = useCarSharingRepository();
    const filterDefs = useRecoilValue(STATE_ACTIVE_FILTERS);
    const [updating, setUpdating] = useRecoilState(STATE_TABLE_UPDATING);
    const correlationIdStrict = !!correlationId;
    const {dispatchGlobalData} = useContext(GlobalContext);

    const setSummaryUpdating = useSetRecoilState(STATE_SUMMARY_UPDATING);
    const agGridLocale = useAgGridLocale();
    const carSharing : boolean = props.carsharing || false;

    const gridRef = useRef<AgGridReact>(null);

    const columnDefs: ColDef[] = [
        {
            field: "createdDate",
            headerName: t("table.creationDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: "modifiedDate",
            headerName: t("table.modificationDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: "lastTransactionDate",
            headerName: t("table.lastTransactionDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: "maskeredPan",
            headerName: t("table.maskeredPan"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params) => (params.value ? params.value : "---"),
        },
        {
            field: "type",
            headerName: t("table.paymentType"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            lockVisible: true,
            valueFormatter: (params) =>
                Const.convertTypology(Const.paymentTypes, params.value)
                    ? t(Const.convertTypology(Const.paymentTypes, params.value))
                    : "---",
        },
        {
            field: "amount",
            headerName: t("table.amount"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: (params) => formatCurrency(params.value, "EUR", 0.01),
        },
        {
            field: "amountType",
            headerName: t("table.amountType"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            cellRenderer: (params: any) =>
                params.value
                    ? t(
                        Const.convertAmountType(
                            Const.amountTypes,
                            params.value,
                            params.data.usePreAuth
                        )
                    )
                    : "---",
        },
        {
            field: "preauthAmountReleased",
            headerName: t("table.releasedAmount"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            cellRenderer: (params: any) =>
                params.value !== null && params.value !== undefined
                    ? formatCurrency(params.value, "EUR", 0.01)
                    : "---",
        },
        {
            field: "dueDate",
            headerName: t("table.dueDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDateWithoutTime(params.value),
        },
        {
            field: "invoiceDate",
            headerName: t("table.invoiceDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDateWithoutTime(params.value),
        },
        {
            field: "invoiceNumber",
            headerName: t("table.invoiceNumber"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params) => (params.value ? params.value : "---"),
        },
        {
            field: "sapInvoiceNumber",
            headerName: t("table.sapInvoiceNumber"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: (params) => (params.value ? params.value : "---"),
        },
        {
            field: "customerInvoice.firstName",
            headerName: t("table.customerName"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: (params) =>
                params?.value
                    ? params.value
                    : params.data?.customerInvoice
                        ? "---"
                        : params.data.customerName,
        },
        {
            field: "customerInvoice.lastName",
            headerName: t("table.customerLastName"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: (params) => (params.value ? params.value : "---"),
        },
        {
            field: "customerInvoice.companyName",
            headerName: t("table.businessName"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: (params) => (params.value ? params.value : "---"),
        },
        {
            field: "customerId",
            headerName: t("table.customerId"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params) => (params.value ? params.value : "---"),
        },
        {
            field: "cardTerminalType",
            headerName: t("table.paymentMode"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params) =>
                params.value
                    ? t(Const.convertTypology(Const.paymentTMode, params.value))
                    : "---",
        },
        {
            field: "lastTransactionId",
            headerName: t("table.lastTransactionId"),
            minWidth: 240,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: (params) => (params.value ? params.value + "" : "---"),
        },
        {
            field: "numAttemptsUnpaid",
            headerName: t("table.retry"),
            minWidth: 240,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: (params) => (params.value ? params.value : "---"),
        },
        {
            field: "correlationName",
            headerName: t("table.correlationName"),
            minWidth: 240,
            unSortIcon: true,
            sortable: true,
            tooltipValueGetter: (params: ITooltipParams) => params.value,
            cellRenderer: (params: any) => Const.checkCorrelationName(params, t),
        },
        {
            field: "correlationId",
            headerName: t("table.correlationId"),
            minWidth: 240,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params) => Const.checkCorrelationID(params, t),
        },
        {
            field: "status",
            headerName: t("table.status"),
            minWidth: 150,
            unSortIcon: true,
            sortable: true,
            pinned: "right",
            lockPosition: "right",
            lockVisible: true,
            lockPinned: true,
            cellRendererSelector: (params) => {
                let status: string = params.data.status;
                status = handleStatus(params.data);
                let color = handleColour(params.data);
                if (status == "OK") status = "SUCCESS";
                return {
                    component: () => <ColouredChip name={status} color={color}/>,
                };
            },
        },
        {
            field: "dealer",
            headerName: t("table.dealer"),
            minWidth: 180,
            unSortIcon: true,
            sortable: true
        },
        {
            field: "legalEntity",
            headerName: t("table.legalEntity"),
            minWidth: 180,
            unSortIcon: true,
            sortable: true,
        },
        {
            field: "_actions",
            headerName: "",
            suppressSizeToFit: true,
            minWidth: 100,
            maxWidth: 110,
            sortable: false,
            pinned: "right",
            lockPosition: "right",
            lockVisible: true,
            lockPinned: true,
            cellRendererSelector: (params) => {
                return {
                    component: () => {
                        if (!hasScope(PAYMENT_REQUEST_VIEW_DETAIL)) {
                            return null;
                        }

                        return (
                            <FinanceDynamicActions>
                                <Tooltip
                                    arrow
                                    placement={"top"}
                                    title={t("table.actions.detail")}
                                >
                                    <IconButton
                                        color="accent"
                                        onClick={() => {
                                            carSharing ?
                                                handleRedirect(params.data.csCustomerId!, 'PAYMENTS')
                                                :
                                                window.open(
                                                    `/finance/payments/payment-request/${params.data.id}`,
                                                    "_blank"
                                                );
                                        }}
                                    >
                                        <DescriptionRounded/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip
                                    arrow
                                    placement={"top"}
                                    title={t("table.actions.downloadInvoice")}
                                >
                                    <IconButton
                                        disabled={props.carsharing || !params.data.invoiceNumber}
                                        color="accent"
                                        onClick={() => {
                                            downloadInvoice(params.data.invoiceNumber).then((res) => {
                                                if (res?.result) {
                                                    let pdfWindow = window.open("");
                                                    pdfWindow!.document.write(
                                                        `<iframe width='100%' height='100%' src='data:application/pdf;base64, ${encodeURI(
                                                            res.result
                                                        )}'></iframe>`
                                                    );
                                                } else {
                                                    dispatchGlobalData({
                                                        type: setSnackbar,
                                                        payload: {
                                                            snackbar: {
                                                                message: t("table.actions." + res?.message),
                                                                type: snackbarTypes.error,
                                                                show: true,
                                                                autoClose: true,
                                                            },
                                                        },
                                                    });
                                                }
                                            });
                                        }}
                                    >
                                        <DownloadRoundedIcon/>
                                    </IconButton>
                                </Tooltip>
                            </FinanceDynamicActions>
                        );
                    },
                };
            },
            suppressFiltersToolPanel: true,
        },
    ]

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data["id"],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: {
            title: t("table.no-rows.title"),
            content: t("table.no-rows.content"),
        },
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(
            createDatasource(filterDefs, gridRef.current.api)
        );
        setSummaryUpdating(true);
    }, [filterDefs]);

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(
                createDatasource(filterDefs, gridRef.current.api)
            );
            setUpdating(false);
            setSummaryUpdating(true);
        }
    }, [updating]);

    useEffect(() => {
        setSummaryUpdating(true);
    }, [agGridLocale]);

    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort =
                    params.request.sortModel.length === 0
                        ? null
                        : params.request.sortModel[0].colId;
                let order =
                    params.request.sortModel.length === 0
                        ? null
                        : params.request.sortModel[0].sort.toUpperCase();
                if (carSharing) {
                    getPaymentsRequestsCs(
                        startRow,
                        endRow - startRow,
                        sort,
                        order,
                        filterDefs,
                        customerId,
                        invoiceNumber,
                        correlationName,
                        correlationId,
                        correlationIdStrict,
                        false
                    ).then((response) => {
                        let result = response.result;
                        if (result.request.totalElements === 0) {
                            api.showNoRowsOverlay();
                        } else {
                            api.hideOverlay();
                        }

                        params.success({
                            rowData: result.content,
                            rowCount: result.request.totalElements,
                        });
                    }).catch(() => params.fail());
                } else {
                    getPaymentsRequests(
                        startRow,
                        endRow - startRow,
                        sort,
                        order,
                        filterDefs,
                        customerId,
                        invoiceNumber,
                        correlationName,
                        correlationId,
                        correlationIdStrict,
                        false
                    )
                        .then((response) => {
                            let result = response.result;
                            if (result["total-amount"] === 0) {
                                api.showNoRowsOverlay();
                            } else {
                                api.hideOverlay();
                            }

                            params.success({
                                rowData: result.content,
                                rowCount: result["total-amount"],
                            });
                        })
                        .catch(() => params.fail());
                }
            },
        };
    };

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    const toolbar = {
        renderInfo: () => (
            <NumericSummary
                query={() => carSharing ? getSummaryRequestCs(filterDefs, false) : getSummaryRequest(filterDefs, false)}
                fields={carSharing ? ['accountedCount', 'sentCount'] : Const.getSummaryFields()}
                i18nPath={"payments.payment-request.statuses"}
            />
        ),
        renderActions: () => (
            <>
                <Button
                    fullWidth
                    color={"accent"}
                    variant="contained"
                    component="label"
                    onClick={exportAsExcel}
                >
                    <FileDownloadRounded/> {t("exportPaymentRequest")}
                </Button>
            </>
        ),
    };

    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState(),
        });
    }

    function getExportableExcelColumns(): string[] {
        let keys: string[] = gridRef?.current?.columnApi
            .getAllDisplayedColumns()
            .map((column) => column.getColId()) as string[];
        let amountIndex: number = keys.findIndex((column) => column === "status");

        return keys;
    }

    function exportAsExcel() {
        gridRef?.current?.api.exportDataAsExcel({
            ...ExportDataAsExcelBase,
            author: "Finance - Payments",
            fileName: `PaymentRequest - ${dayjs().format("L")}.xlsx`,
            sheetName: `PaymentRequest`,
            columnKeys: getExportableExcelColumns(),
            processCellCallback: (params) => {
                if (
                    params.column.getColId() === "amount" ||
                    params.column.getColId() === "preauthAmountReleased"
                ) {
                    if (params.value) {
                        const numericValue = parseFloat(params.value) / 100;
                        return numericValue.toFixed(2) + " €";
                    } else {
                        return "";
                    }
                }

                if (params.column.getColId() === "amountType") {
                    return t(Const.convertTypology(Const.amountTypes, params.value));
                }
                if (params.column.getColId() === "type" && params?.node?.data?.type) {
                    return Const.convertTypology(Const.paymentTypes, params.value)
                        ? t(Const.convertTypology(Const.paymentTypes, params.value))
                        : "---";
                }
                if (
                    params.column.getColId() === "invoiceDate" ||
                    params.column.getColId() === "dueDate"
                ) {
                    return formatDateWithoutTime(params.value);
                }
                if (
                    params.column.getColId() === "lastTransactionDate" ||
                    params.column.getColId() === "modifiedDate" ||
                    params.column.getColId() === "createdDate"
                ) {
                    return formatDate(params.value);
                }
                if (params.column.getColId() === "status") {
                    let status: string = params.value;
                    let amountType = params.node?.data?.amountType;
                    let preauthIsReleased = params.node?.data?.preauthIsReleased;

                    status = preauthIsReleased && amountType === "PRE_AUTH"
                        ? "RELEASED"
                        : (preauthIsReleased && amountType === "DEPOSIT" ? "RELEASED_DEPOSIT"
                            : amountType === "PRE_AUTH" ? "OPENED" : amountType === "DEPOSIT" ? "OPENED_DEPOSIT"
                                : status);
                    return t("statuses." + status);
                }
                let raCode = params.node?.data?.raCode;
                if (raCode && params.column.getColId() === "correlationName") {
                    return "RA";
                }
                if (raCode && params.column.getColId() === "correlationId") {
                    return raCode;
                } else if (!raCode) {
                    return params.value;
                }

                return params.value;
            },
        });
    }

    const excelStyles: ExcelStyle[] = [
        {
            id: "typeString",
            dataType: "String",
        },
    ];

    return (
        <>
            <AgGridCustomTable
                toolbar={toolbar}
                rowModelType="serverSide"
                paginationPageSize={getLocalGridState(UNIQUE_ID).rows}
                serverSideInfiniteScroll
                gridRef={gridRef}
                columnDefs={columnDefs}
                excelStyles={excelStyles}
                gridOptions={gridOptions}
                rowSelection={"multiple"}
                suppressRowClickSelection
                noRowsOverlayComponent={noRowsOverlayComponent}
                onGridReady={() => {
                    gridRef.current!.api.setServerSideDatasource(
                        createDatasource(filterDefs, gridRef.current!.api)
                    );

                    let gridState = getLocalGridState(UNIQUE_ID).cols;
                    if (gridState !== undefined) {
                        gridRef.current!.columnApi.applyColumnState({
                            state: gridState,
                            applyOrder: true,
                        });
                    }

                    gridRef.current!.columnApi.setColumnVisible("preauthAmountReleased", !carSharing);
                    gridRef.current!.columnApi.setColumnVisible("numAttemptsUnpaid", !carSharing);
                    gridRef.current!.columnApi.setColumnVisible("correlationName", !carSharing);
                    gridRef.current!.columnApi.setColumnVisible("cardTerminalType", !carSharing);
                    gridRef.current!.columnApi.setColumnVisible("dueDate", !carSharing);
                    gridRef.current!.columnApi.setColumnVisible("dealer", !carSharing);
                }}
                onColumnMoved={(e) => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnPinned={(e) => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnResized={(e) => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onSortChanged={(e) => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onPaginationChanged={() => {
                    if (gridRef.current!.api) {
                        setLocalGridState(UNIQUE_ID, {
                            rows: gridRef.current!.api?.paginationGetPageSize(),
                            cols: getLocalGridState(UNIQUE_ID).cols,
                        });
                    }
                }}
            ></AgGridCustomTable>
        </>
    );
};

export default PaymentRequestTable;
